import type { ApiClient } from './client';
import type {
  Achievement,
  AchievementHolder,
  AlbumMediaItem,
  AlbumType,
  BuildProfile,
  Contributor,
  DynoSummary,
  GlobalSearchResults,
  MediaAlbum,
  MediaModificationLink,
  MediaRelationshipType,
  NotificationPage,
  NotificationPrefs,
  PerformanceRecord,
  PerformanceRecordInput,
  Shop,
  WatchStatus,
} from '../../types/buildExpansion';

interface PerfRecordTypeMeta {
  key: string;
  label: string;
  units: string[];
  direction: 'asc' | 'desc';
}

export const buildExpansionMethods = {
  // ─── Achievements ───────────────────────────────────────────────────────────

  async getVehicleAchievements(vehicleId: number, includeHistorical = true): Promise<Achievement[]> {
    const qs = new URLSearchParams({
      vehicle_id: String(vehicleId),
      include_historical: includeHistorical ? '1' : '0',
    });
    const response = await this.makeRequest<{ achievements?: Achievement[] }>(
      `/vehicle_achievements.php?${qs}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data?.achievements ?? [];
  },

  async getAchievementHolders(
    definitionSlug: string,
    status: 'current' | 'historical' = 'current'
  ): Promise<{ definition: Record<string, unknown> | null; holders: AchievementHolder[] }> {
    const qs = new URLSearchParams({ definition: definitionSlug, status });
    const response = await this.makeRequest<{
      definition?: Record<string, unknown>;
      holders?: AchievementHolder[];
    }>(`/vehicle_achievements.php?${qs}`);
    return {
      definition: response.data?.definition ?? null,
      holders: response.data?.holders ?? [],
    };
  },

  // ─── Performance records ────────────────────────────────────────────────────

  async getPerformanceRecordList(
    vehicleId: number,
    recordType?: string
  ): Promise<{ records: PerformanceRecord[]; record_types: PerfRecordTypeMeta[] }> {
    const qs = new URLSearchParams({ vehicle_id: String(vehicleId) });
    if (recordType) qs.set('type', recordType);
    const response = await this.makeRequest<{
      records?: PerformanceRecord[];
      record_types?: PerfRecordTypeMeta[];
    }>(`/vehicle_performance_records.php?${qs}`, { headers: this.getAuthHeaders() });
    return {
      records: response.data?.records ?? [],
      record_types: response.data?.record_types ?? [],
    };
  },

  async getDynoSummary(vehicleId: number): Promise<DynoSummary | null> {
    const response = await this.makeRequest<{ dyno?: DynoSummary }>(
      `/vehicle_performance_records.php?vehicle_id=${vehicleId}&view=dyno`,
      { headers: this.getAuthHeaders() }
    );
    return response.data?.dyno ?? null;
  },

  async createExpandedPerformanceRecord(input: PerformanceRecordInput): Promise<number> {
    const response = await this.makeRequest<{ id?: number }>('/vehicle_performance_records.php', {
      method: 'POST',
      body: JSON.stringify(input),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to save performance record');
    }
    return Number(response.data?.id ?? 0);
  },

  async updateExpandedPerformanceRecord(
    recordId: number,
    input: Partial<PerformanceRecordInput> & { vehicle_id: number }
  ): Promise<void> {
    const response = await this.makeRequest('/vehicle_performance_records.php', {
      method: 'PUT',
      body: JSON.stringify({ ...input, record_id: recordId }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to update performance record');
    }
  },

  async archivePerformanceRecord(vehicleId: number, recordId: number): Promise<void> {
    const response = await this.makeRequest('/vehicle_performance_records.php', {
      method: 'DELETE',
      body: JSON.stringify({ vehicle_id: vehicleId, record_id: recordId }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to archive record');
    }
  },

  // ─── Media albums and media-mod links ───────────────────────────────────────

  async getMediaAlbums(vehicleId: number): Promise<{
    albums: MediaAlbum[];
    media_links: MediaModificationLink[];
    album_types: AlbumType[];
    relationship_types: MediaRelationshipType[];
  }> {
    const response = await this.makeRequest<{
      albums?: MediaAlbum[];
      media_links?: MediaModificationLink[];
      album_types?: AlbumType[];
      relationship_types?: MediaRelationshipType[];
    }>(`/vehicle_media_albums.php?vehicle_id=${vehicleId}`, { headers: this.getAuthHeaders() });
    return {
      albums: response.data?.albums ?? [],
      media_links: response.data?.media_links ?? [],
      album_types: response.data?.album_types ?? [],
      relationship_types: response.data?.relationship_types ?? [],
    };
  },

  async getAlbumMedia(vehicleId: number, albumId: number): Promise<AlbumMediaItem[]> {
    const response = await this.makeRequest<{ media?: AlbumMediaItem[] }>(
      `/vehicle_media_albums.php?vehicle_id=${vehicleId}&album_id=${albumId}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data?.media ?? [];
  },

  async mediaAlbumAction(
    vehicleId: number,
    action:
      | 'create'
      | 'update'
      | 'delete'
      | 'add_media'
      | 'remove_media'
      | 'reorder'
      | 'set_cover'
      | 'add_video'
      | 'link_modification'
      | 'unlink_modification',
    payload: Record<string, unknown> = {}
  ): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>('/vehicle_media_albums.php', {
      method: 'POST',
      body: JSON.stringify({ vehicle_id: vehicleId, action, ...payload }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Media update failed');
    }
    return response.data ?? {};
  },

  // ─── Build profile ──────────────────────────────────────────────────────────

  async getBuildProfile(vehicleId: number): Promise<{
    profile: BuildProfile | null;
    options?: Record<string, string[]>;
  }> {
    const response = await this.makeRequest<{
      profile?: BuildProfile | null;
      options?: Record<string, string[]>;
    }>(`/vehicle_build_profile.php?vehicle_id=${vehicleId}`, { headers: this.getAuthHeaders() });
    return {
      profile: response.data?.profile ?? null,
      options: response.data?.options,
    };
  },

  async saveBuildProfile(
    vehicleId: number,
    input: Partial<BuildProfile>
  ): Promise<BuildProfile | null> {
    const response = await this.makeRequest<{ profile?: BuildProfile | null }>(
      '/vehicle_build_profile.php',
      {
        method: 'POST',
        body: JSON.stringify({ vehicle_id: vehicleId, ...input }),
        headers: this.getAuthHeaders(),
      }
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to save build profile');
    }
    return response.data?.profile ?? null;
  },

  // ─── Shops and contributors ─────────────────────────────────────────────────

  async searchShops(query: string, limit = 12): Promise<Shop[]> {
    const qs = new URLSearchParams({ view: 'search', q: query, limit: String(limit) });
    const response = await this.makeRequest<{ shops?: Shop[] }>(`/shops.php?${qs}`);
    return response.data?.shops ?? [];
  },

  async getShopDirectory(params: {
    q?: string;
    page?: number;
    city?: string;
    state?: string;
    service?: string;
    verification?: string;
  } = {}): Promise<{
    shops: Shop[];
    total?: number;
    total_pages?: number;
  }> {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.page) qs.set('page', String(params.page));
    if (params.city) qs.set('city', params.city);
    if (params.state) qs.set('state', params.state);
    if (params.service) qs.set('service', params.service);
    if (params.verification) qs.set('verification', params.verification);
    const query = qs.toString();
    const response = await this.makeRequest<{
      shops?: Shop[];
      total?: number;
      total_pages?: number;
    }>(`/shops.php${query ? `?${query}` : ''}`);
    return {
      shops: response.data?.shops ?? [],
      total: response.data?.total,
      total_pages: response.data?.total_pages,
    };
  },

  async getShopBySlug(slug: string): Promise<{
    shop: Shop | null;
    portfolio: Record<string, unknown>[];
    can_manage: boolean;
    can_act_as_shop: boolean;
  }> {
    const response = await this.makeRequest<{
      shop?: Shop | null;
      portfolio?: Record<string, unknown>[];
      can_manage?: boolean;
      can_act_as_shop?: boolean;
    }>(`/shops.php?slug=${encodeURIComponent(slug)}`);
    if (!response.success) {
      throw new Error(response.error || 'Shop not found');
    }
    return {
      shop: response.data?.shop ?? null,
      portfolio: response.data?.portfolio ?? [],
      can_manage: Boolean(response.data?.can_manage),
      can_act_as_shop: Boolean(response.data?.can_act_as_shop),
    };
  },

  async uploadShopLogo(shopId: number, file: File): Promise<{ logo_path: string }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const formData = new FormData();
    formData.append('shop_id', String(shopId));
    formData.append('logo', file);
    const response = await this.makeRequest<{ logo_path: string }>('/shop_logo_upload.php', {
      method: 'POST',
      body: formData,
    });
    if (response.success && response.data?.logo_path) return response.data;
    throw new Error(response.error || response.message || 'Failed to upload shop photo');
  },

  async deleteShopLogo(shopId: number): Promise<void> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest('/shop_logo_upload.php', {
      method: 'DELETE',
      body: JSON.stringify({ shop_id: shopId }),
    });
    if (!response.success) {
      throw new Error(response.error || response.message || 'Failed to remove shop photo');
    }
  },

  async updateShopProfile(
    shopId: number,
    payload: Record<string, unknown>,
  ): Promise<Shop> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<{ shop?: Shop }>('/shop_update.php', {
      method: 'POST',
      body: JSON.stringify({ shop_id: shopId, ...payload }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success || !response.data?.shop) {
      throw new Error(response.error || 'Failed to update shop profile');
    }
    return response.data.shop;
  },

  async getMyShops(): Promise<Shop[]> {
    const response = await this.makeRequest<{ shops?: Shop[] }>('/shops.php?view=mine', {
      headers: this.getAuthHeaders(),
    });
    return response.data?.shops ?? [];
  },

  async submitShopRequest(input: Record<string, unknown>): Promise<{ id: number }> {
    const response = await this.makeRequest<{ id?: number }>('/shops.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'request', ...input }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to submit shop request');
    }
    return { id: Number(response.data?.id ?? 0) };
  },

  async getMyShopRequests(): Promise<import('../../types/buildExpansion').ShopRequest[]> {
    const response = await this.makeRequest<{ requests?: import('../../types/buildExpansion').ShopRequest[] }>(
      '/shops.php?view=my_requests',
      { headers: this.getAuthHeaders() }
    );
    return response.data?.requests ?? [];
  },

  async checkShopDuplicates(name: string, city?: string): Promise<Shop[]> {
    const qs = new URLSearchParams({ view: 'duplicates', name });
    if (city) qs.set('city', city);
    const response = await this.makeRequest<{ duplicates?: Shop[] }>(`/shops.php?${qs}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data?.duplicates ?? [];
  },

  /** @deprecated Direct create disabled — use submitShopRequest */
  async createShop(_input: Record<string, unknown>): Promise<{ id: number; slug: string }> {
    throw new Error('Direct shop creation is disabled. Submit a shop request instead.');
  },

  async getVehicleContributors(vehicleId: number): Promise<Contributor[]> {
    const response = await this.makeRequest<{ contributors?: Contributor[] }>(
      `/vehicle_contributors.php?vehicle_id=${vehicleId}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data?.contributors ?? [];
  },

  async getPendingContributorRequests(): Promise<Contributor[]> {
    const response = await this.makeRequest<{ pending?: Contributor[] }>(
      '/vehicle_contributors.php?view=pending',
      { headers: this.getAuthHeaders() }
    );
    return response.data?.pending ?? [];
  },

  async contributorAction(
    action: 'create' | 'accept' | 'decline' | 'revoke',
    payload: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>('/vehicle_contributors.php', {
      method: 'POST',
      body: JSON.stringify({ action, ...payload }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Contributor update failed');
    }
    return response.data ?? {};
  },

  // ─── Global search ──────────────────────────────────────────────────────────

  async globalSearch(query: string, limit = 5): Promise<GlobalSearchResults> {
    const qs = new URLSearchParams({ q: query, limit: String(limit) });
    const response = await this.makeRequest<{ results?: GlobalSearchResults }>(
      `/global_search.php?${qs}`,
      { headers: this.getAuthHeaders() }
    );
    const results = response.data?.results;
    return results && typeof results === 'object' ? results : {};
  },

  // ─── Watch and notifications ────────────────────────────────────────────────

  async getWatchStatus(vehicleId: number): Promise<WatchStatus> {
    if (!this.isAuthenticated()) {
      return { is_favorited: false, watching: false };
    }
    const response = await this.makeRequest<WatchStatus & { enabled?: boolean }>(
      `/vehicle_watch.php?vehicle_id=${vehicleId}`,
      { headers: this.getAuthHeaders() }
    );
    return {
      is_favorited: Boolean(response.data?.is_favorited),
      watching: Boolean(response.data?.watching),
    };
  },

  async setWatchStatus(vehicleId: number, watch: boolean): Promise<boolean> {
    const response = await this.makeRequest<{ watching?: boolean }>('/vehicle_watch.php', {
      method: 'POST',
      body: JSON.stringify({ vehicle_id: vehicleId, watch }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to update watch status');
    }
    return Boolean(response.data?.watching);
  },

  async getNotifications(page = 1, unreadOnly = false): Promise<NotificationPage> {
    const qs = new URLSearchParams({ page: String(page) });
    if (unreadOnly) qs.set('unread_only', '1');
    const response = await this.makeRequest<NotificationPage>(`/notifications.php?${qs}`, {
      headers: this.getAuthHeaders(),
    });
    return (
      response.data ?? { items: [], page, limit: 20, total: 0, unread: 0 }
    );
  },

  async markNotificationsRead(ids?: number[]): Promise<number> {
    const response = await this.makeRequest<{ marked?: number }>('/notifications.php', {
      method: 'POST',
      body: JSON.stringify(ids ? { action: 'mark_read', ids } : { action: 'mark_all_read' }),
      headers: this.getAuthHeaders(),
    });
    return Number(response.data?.marked ?? 0);
  },

  async getNotificationPrefs(): Promise<NotificationPrefs> {
    const response = await this.makeRequest<{ prefs?: NotificationPrefs }>(
      '/notifications.php?view=prefs',
      { headers: this.getAuthHeaders() }
    );
    return response.data?.prefs ?? {};
  },

  async setNotificationPrefs(prefs: NotificationPrefs): Promise<void> {
    const response = await this.makeRequest('/notifications.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'set_prefs', prefs }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to save notification preferences');
    }
  },

  async getWebPushConfig(): Promise<{ enabled?: boolean; vapid_public_key?: string | null }> {
    const response = await this.makeRequest<{ enabled?: boolean; vapid_public_key?: string | null }>(
      '/web_push_subscribe.php',
      { headers: this.getAuthHeaders() }
    );
    return response.data ?? {};
  },

  async subscribeWebPush(body: Record<string, unknown>): Promise<void> {
    const response = await this.makeRequest('/web_push_subscribe.php', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to subscribe to browser notifications');
    }
  },

  async getMaintenance(vehicleId: number): Promise<{
    items: Array<Record<string, unknown>>;
    upcoming: Array<Record<string, unknown>>;
    vehicle: Record<string, unknown>;
    is_premium?: boolean;
  }> {
    const response = await this.makeRequest<{
      items?: Array<Record<string, unknown>>;
      upcoming?: Array<Record<string, unknown>>;
      vehicle?: Record<string, unknown>;
      is_premium?: boolean;
    }>(`/maintenance.php?vehicle_id=${vehicleId}`, { headers: this.getAuthHeaders() });
    return {
      items: response.data?.items ?? [],
      upcoming: response.data?.upcoming ?? [],
      vehicle: response.data?.vehicle ?? {},
      is_premium: response.data?.is_premium,
    };
  },

  async postMaintenance(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>('/maintenance.php', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      const err = new Error(response.error || 'Maintenance request failed') as Error & {
        code?: string;
        capability?: string;
      };
      err.code = (response as { code?: string }).code;
      err.capability = (response as { capability?: string }).capability;
      throw err;
    }
    return response.data ?? {};
  },

  async uploadMaintenanceDocument(maintenanceId: number, file: File): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('maintenance_id', String(maintenanceId));
    formData.append('document', file);
    const response = await this.makeRequest<{ data?: Record<string, unknown> }>(
      '/maintenance_document_upload.php',
      {
        method: 'POST',
        body: formData,
        headers: this.getAuthHeaders(),
      }
    );
    if (!response.success) {
      const err = new Error(response.error || 'Document upload failed') as Error & { code?: string };
      err.code = (response as { code?: string }).code;
      throw err;
    }
    return response.data?.data ?? response.data ?? {};
  },

  async getMaintenanceVehicleRecord(vehicleId: number): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>(
      `/maintenance.php?vehicle_id=${vehicleId}&view=vehicle_record`,
      { headers: this.getAuthHeaders() }
    );
    if (!response.success) {
      const err = new Error(response.error || 'Could not build Vehicle Record') as Error & { code?: string };
      err.code = (response as { code?: string }).code;
      throw err;
    }
    return response.data ?? {};
  },

  async getAdminPremiumMetrics(): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>('/admin_premium.php', {
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to load Premium metrics');
    }
    return response.data ?? {};
  },

  async adminGrantPremium(payload: {
    user_id: number;
    reason: string;
    days?: number;
    capability?: string;
  }): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>('/admin_premium.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'grant', ...payload }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Grant failed');
    }
    return response.data ?? {};
  },

  async adminRevokePremiumGrant(payload: {
    user_id: number;
    reason?: string;
  }): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>('/admin_premium.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'revoke', ...payload }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Revoke failed');
    }
    return response.data ?? {};
  },

  async getAdminUserPremiumEntitlements(userId: number): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>(
      `/admin_premium.php?user_id=${userId}`,
      { headers: this.getAuthHeaders() }
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to load entitlements');
    }
    return response.data ?? {};
  },

  async getFuelEntries(vehicleId: number): Promise<{
    items: Array<Record<string, unknown>>;
    summary: Record<string, unknown>;
    is_premium?: boolean;
  }> {
    const response = await this.makeRequest<{
      items?: Array<Record<string, unknown>>;
      summary?: Record<string, unknown>;
      is_premium?: boolean;
    }>(`/fuel.php?vehicle_id=${vehicleId}`, { headers: this.getAuthHeaders() });
    return {
      items: response.data?.items ?? [],
      summary: response.data?.summary ?? {},
      is_premium: response.data?.is_premium,
    };
  },

  async postFuelEntry(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>('/fuel.php', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      const err = new Error(response.error || 'Fuel request failed') as Error & {
        code?: string;
        capability?: string;
      };
      err.code = (response as { code?: string }).code;
      err.capability = (response as { capability?: string }).capability;
      throw err;
    }
    return response.data ?? {};
  },

  async adminUpdatePremiumSettings(payload: { trial_days: number }): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>('/admin_premium.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'update_settings', trial_days: payload.trial_days }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Could not save Premium settings');
    }
    return response.data ?? {};
  },

  async getPremiumStatus(): Promise<Record<string, unknown>> {
    const response = await this.makeRequest<Record<string, unknown>>('/premium.php', {
      headers: this.getAuthHeaders(),
    });
    return response.data ?? {};
  },

  async premiumCheckout(planKey: string, successUrl?: string, cancelUrl?: string): Promise<{ checkout_url?: string }> {
    const response = await this.makeRequest<{ checkout_url?: string }>('/premium.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'checkout', plan_key: planKey, success_url: successUrl, cancel_url: cancelUrl }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Checkout failed');
    }
    return response.data ?? {};
  },

  async premiumBillingPortal(returnUrl?: string): Promise<{ url?: string }> {
    const response = await this.makeRequest<{ url?: string }>('/premium.php', {
      method: 'POST',
      body: JSON.stringify({ action: 'portal', return_url: returnUrl }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.error || 'Billing portal failed');
    }
    return response.data ?? {};
  },
} satisfies ThisType<ApiClient>;
